// @flow

import React, { type Node, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { useOpenedIncomingPayment, getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useUniqueId } from '@tectonics/hooks';

import { RadioButton, Separator, Text } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';

import { getSupportedTraits } from '@tectonics/prs/feature';
import lookAndFeel from '@tectonics/look-and-feel/volcanes';
import { intersect } from '@tectonics/commons/arrays';
import { getSafeExternalPayProviderPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';
import { translate } from '@tectonics/translate';

import bancolombiaButtonIconSrc from './icons/bancolombia-button-icon.svg';
import achpseIconSrc from './icons/achpse-icon.svg';

export function ExternalPayProviderList(): Node {
  const incomingPayment = useOpenedIncomingPayment();
  let checkoutPaymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const externalpayproviderAllowedBrands = getSafeExternalPayProviderPaymentOption(checkoutPaymentOptions);

  const supportedDisplayExternalPayProviderBrandFeature = getSafeSupportedFeatureByName(
    'display_externalpayprovider_accepts_brand',
    lookAndFeel.prsSupport
  );

  const supportedBrands = getSupportedTraits(supportedDisplayExternalPayProviderBrandFeature);
  const brandsToDisplay = intersect(supportedBrands, externalpayproviderAllowedBrands.map(brand => brand.value));

  const [selectedExternalPayProvider, setSelectedExternalPayProvider] = useUserInputValue('selectedExternalPayProvider');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedExternalPayProvider');
  const submitting = useSubmitting();

  const inputId = useUniqueId();
  const onChange = useCallback(e => setSelectedExternalPayProvider(e.target.value), [setSelectedExternalPayProvider]);

  useEffect(() => {
    if(selectedExternalPayProvider === '' && brandsToDisplay.length === 1) {
      setSelectedExternalPayProvider(brandsToDisplay[0]);
    }
  }, [selectedExternalPayProvider]);

  return (
    <div data-test-name="externalpayprovider-code">
      <Text flavor="info">
        {translate('volcanes.payment-methods.externalpayprovider.externalPayProviderSelector.label')}
      </Text>
      <Separator direction="vertical" size={4}/>
      {brandsToDisplay.map(brand => (
        <div key={brand}>
          <Label htmlFor={`${inputId}-${brand}`}>
            <RadioButton
              id={`${inputId}-${brand}`}
              name="externalpayprovider_code"
              value={brand}
              checked={brand === selectedExternalPayProvider}
              onChange={onChange}
              disabled={submitting}
              data-error={formHasBeenSent && validationError ? 'error' : null}
              data-test-value={brand}
            />

            <Separator direction="h" size={4} />
            <ExternalPayProviderIcon src={getExternalPayProviderIconSrc(brand)} alt={brand} />
            <Separator direction="h" size={4} />

            <span>{getExternalPayProviderName(brand)}</span>
          </Label>
        </div>
      ))}

      {formHasBeenSent && validationError && (
        <ErrorLabel>El método de transferencia electrónica elijido es inválido</ErrorLabel>
      )}

      {selectedExternalPayProvider === 'achpse' && (
        <Separator direction="vertical" size={4}/>
      )}
    </div>
  );
}

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
`;

const ExternalPayProviderIcon = styled.img``;

function getExternalPayProviderName(externalpayprovider: string): string {
  switch(externalpayprovider) {
    case 'bancolombia-button':
      return 'Botón Bancolombia';

    case 'achpse':
      return 'PSE Avanza';

    default:
      throw new Error(`ExternalPayProviderList - external pay provider not found ${externalpayprovider}`);
  }
}

function getExternalPayProviderIconSrc(externalpayprovider: string): string {
  switch(externalpayprovider) {
    case 'bancolombia-button':
      return bancolombiaButtonIconSrc;

    case 'achpse':
      return achpseIconSrc;

    default:
      throw new Error(`ExternalPayProviderList - Image source not found for ${externalpayprovider}`);
  }
}
